import React, { useState } from 'react';
import { IButton, IButtonColors, IThemes } from "types/base";
import cn from "classnames";
import { formatNumberToCurrency, getCurrencySignFromCurrency } from "helpers";
import useRouter from "components/hooks/useRouter";
import useTrans from "components/hooks/useTrans";
import Text from "components/typography/Text";
import PlanParagraph from "components/elements/PlanParagraph";
import PlanButton from "components/elements/PlanButton";
import InternalPageLink from "components/elements/InternalPageLink";
import Button from "components/elements/Button";
import { localIcon } from "components/elements/Icon";
import Link from "components/base/Link";

export type IPricingPlan = {
    id: string,
    name: string,
    mostPopular?: boolean
    startPrice: number,
    theme: IThemes,
    callToAction: IButton[],
    planDescription?: any,
    featureDescription: any,
    onChoosePlan: any;
    onCompareFeatures: any;
    page: string;
};

type Props = {
    previousPlan: IPricingPlan | null;
    index: number;
    currency: string;
    choosePlanLabel?: string;
};

export default function Plan(props: Props & Partial<IPricingPlan>) {
    const {
        startPrice,
        name,
        theme,
        currency,
        mostPopular,
        planDescription,
        featureDescription,
        onChoosePlan,
        onCompareFeatures
    } = props;

    const { t } = useTrans();
    const { locale } = useRouter();
    const [detailsActive, setDetailsActive] = useState(false);
    const planMinHeight = onCompareFeatures ? "min-h-[340px]" : "min-h-[405px]";

    const backgroundCn = cn(
        { "bg-white": theme == "default" },
        { "bg-primary-500": mostPopular },
    );

    const detailsColorCn = cn(
        (mostPopular ? "bg-white" : "bg-primary-500")
    );

    const tagCn = cn(
        "bg-primary-700 text-white",
        "py-1 px-3 rounded-full text-sm mx-auto mb-5"
    );
    const detailsTextColorCn = cn(
        mostPopular ? "text-white" : "text-primary-400"
    );

    const planContainer = cn(
        "flex flex-col rounded-xl content-around py-12 px-12 xl:w-1/4",
        !mostPopular && "border border-primary-50",
        mostPopular ? "lg:py-16 lg:px-10 lg:mt-0" : "lg:my-10 lg:px-10",
        backgroundCn
    );

    const planDescriptionCn = cn(
        "font-light mx-auto text-center mb-8 text-sm",
        mostPopular ? "text-primary-100" : "text-gray-500",
        !startPrice && 'mt-1',
    );

    const featureDescriptionCn = cn(
        mostPopular ? "text-white" : "text-gray-500",
        "text-base"
    );

    const featureDescriptionCnMobile = cn(
        "lg:hidden overflow-hidden transition-all",
        detailsActive ? "flex min-h-60" : "h-0 hidden",
        mostPopular ? "text-white" : "text-gray-500",
        "text-base"
    );

    const buttonContainerCn = cn(
        "flex flex-col w-full justify-center gap-3",
    );
    const buttonCn = cn(
        "flex flex-col mx-auto w-full lg:w-auto"
    );


    return (
        <div className={planContainer}>
            {!startPrice ? (
                <div className={'flex flex-col'}>
                    <div className={"flex flex-col"}>
                        <Text
                            weight={"normal"}
                            className={'mx-auto mb-2'}
                            size={{ sm: "4xl", lg: "5xl" }}
                            color={mostPopular ? "text-white" : "text-gray-900"}
                        >
                            {name}
                        </Text>
                        <div>
                            <PlanParagraph renderAs={"structuredText"} className={planDescriptionCn}>
                                {planDescription}
                            </PlanParagraph>
                        </div>
                    </div>
                    <div className={"flex flex-col text-center"}>
                        <div className={"h-20 w-full mt-5 mb-8 lg:mt-5 lg:mb-8"}>
                            <img
                                src={"/static/icons/icon-pricing-custom.svg"}
                                className={"w-20 h-20 mx-auto"}
                                alt={null}
                            />
                        </div>
                    </div>
                    <div className={`hidden lg:inline-block ${planMinHeight}`}>
                        <PlanParagraph renderAs={"structuredText"} className={featureDescriptionCn}>
                            {featureDescription}
                        </PlanParagraph>
                    </div>
                    <div className={buttonContainerCn}>
                        <Link to={"/demo-request"}>
                            <PlanButton
                                style={'primary'}
                                size={'normal'}
                                label={'Start now'}
                                disabled={false}
                                className={buttonCn}
                            />
                        </Link>
                        <div className={"lg:hidden"}>
                            <Button
                                id={"pricing-plan__see-details"}
                                variant={"link"}
                                size={"medium"}
                                color={detailsColorCn as IButtonColors}
                                icon={localIcon("chevron-right", `${detailsColorCn} mt-px transition-all ${detailsActive && "transform rotate-90"}`)}
                                onClick={() => setDetailsActive(!detailsActive)}
                                className={`mx-auto w-full justify-center ${featureDescriptionCn}`}
                            >
                                {t('seeDetails')}
                            </Button>
                            <div className={detailsActive ? "flex flex-col md:justify-center" : "hidden"}>
                                <PlanParagraph renderAs={"structuredText"} className={featureDescriptionCnMobile}>
                                    {featureDescription}
                                </PlanParagraph>
                            </div>
                        </div>
                        {onCompareFeatures && (
                            <InternalPageLink
                                className={`mx-auto hidden lg:inline-block ${detailsTextColorCn} font-normal cursor-pointer text-center`}
                                onClick={onCompareFeatures}
                            >
                                {t('compareFeatures')}
                            </InternalPageLink>
                        )}
                    </div>
                </div>
            ) : (
                <div>
                    {mostPopular &&
                        <div className={"flex "}>
                                <span className={tagCn}>
                                    {t('mostPopular')}
                                </span>
                        </div>
                    }
                    <div className={"flex flex-col"}>
                        <Text
                            weight={"normal"}
                            className={'mx-auto mb-2'}
                            size={{ sm: "4xl", lg: "5xl" }}
                            color={mostPopular ? "text-white" : "text-gray-900"}
                        >
                            {name}
                        </Text>
                        <div>
                            <PlanParagraph renderAs={"structuredText"} className={planDescriptionCn}>
                                {planDescription}
                            </PlanParagraph>
                        </div>
                    </div>
                    <>
                        <div className={"flex flex-col text-center"}>
                            <Text
                                weight={'semibold'}
                                size={"sm"}
                                color={mostPopular ? "text-primary-200" : "text-primary-400"}
                            >
                                {t('startsAt')}
                            </Text>
                            <div className={"flex mx-auto pr-1"}>
                                <div className={"flex"}>
                                    <Text
                                        weight={'normal'}
                                        size={"2xl"}
                                        className={'mr-1'}
                                        color={mostPopular ? "text-white" : "text-primary-900"}
                                    >
                                        {getCurrencySignFromCurrency(currency)}
                                    </Text>
                                    <Text
                                        weight={'normal'}
                                        size={"5xl"}
                                        className={''}
                                        color={mostPopular ? "text-white" : "text-primary-900"}
                                    >
                                        {formatNumberToCurrency(startPrice, locale)}
                                    </Text>
                                </div>
                            </div>
                            <Text
                                weight={'semibold'}
                                size={"sm"}
                                color={mostPopular ? "text-primary-200" : "text-primary-400"}
                                className={'mb-6 md:mb-9'}
                            >
                                {t('perMonth')}
                            </Text>
                        </div>
                        <div className={`hidden lg:inline-block text-left ${planMinHeight}`}>
                            <PlanParagraph renderAs={"structuredText"} className={featureDescriptionCn}>
                                {featureDescription}
                            </PlanParagraph>
                        </div>
                        <div className={buttonContainerCn}>
                            <PlanButton
                                style={mostPopular ? "default" : "primary"}
                                size={'normal'}
                                label={props.choosePlanLabel ?? 'Choose Plan'}
                                className={buttonCn}
                                disabled={false}
                                onClick={onChoosePlan}
                            />
                            <div className={"lg:hidden"}>
                                <Button
                                    id={"pricing-plan__see-details"}
                                    variant={"link"}
                                    size={"medium"}
                                    color={detailsColorCn as IButtonColors}
                                    icon={localIcon("chevron-right", `${detailsColorCn} mt-px transition-all ${detailsActive && "transform rotate-90"}`)}
                                    onClick={() => setDetailsActive(!detailsActive)}
                                    className={`mx-auto w-full justify-center ${featureDescriptionCn}`}
                                >
                                    {t('seeDetails')}
                                </Button>
                                <div className={detailsActive ? "flex flex-col md:justify-center" : "hidden"}>
                                    <PlanParagraph renderAs={"structuredText"} className={featureDescriptionCnMobile}>
                                        {featureDescription}
                                    </PlanParagraph>
                                </div>

                            </div>
                            {onCompareFeatures && (
                                <InternalPageLink
                                    className={`mx-auto hidden lg:inline-block ${detailsTextColorCn} font-normal cursor-pointer text-center`}
                                    onClick={onCompareFeatures}
                                >
                                    {t('compareFeatures')}
                                </InternalPageLink>
                            )}
                        </div>
                    </>
                </div>
            )}
        </div>
    );
}